// Angular
import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
// RxJS
import { firstValueFrom, from, lastValueFrom, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
// Environment
import { environment } from 'environments/environment';
import { LayoutUtilsService } from './layout-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from 'app/core/ana/authorization';

/**
 * More information there => https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
 */
@Injectable()
export class InterceptService implements HttpInterceptor {

    sesionExpired: boolean = false;

    constructor(
        private router: Router,
        private layoutUtilsService: LayoutUtilsService,
        private translateService: TranslateService,
        private authService: AuthorizationService,
    ) { }

    /**
     * intercept request and add token
     * @param request request to handle
     * @param next request handler
     */
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url.search('AnA-backend/public/login') > -1 ||
            request.url.search('AnA-backend/public/passwordGovernance/changePassword') > -1 ||
			request.url.search('api.openai.com/v1') > -1 ||
            request.url.search('o2c-backend/private/restorationDB/list') > -1 ||
            request.url.search('AnA-backend/public/passwordGovernance/validateEmail') > -1 ||
            request.url.search('AnA-backend/public/passwordGovernance/sendEmail') > -1 ||
            request.url.search('AnA-backend/public/passwordGovernance/validateLink') > -1 ||
            request.url.search('AnA-backend/public/passwordGovernance/registerUsedLink') > -1 ||
            request.url.includes('authorization/isAuthorized')
		) {
            // in case we're not requesting to the login
            return next.handle(request);
        }

        return from(this.handle(request, next));
    }

    private shouldValidateSession(requestUrl: string): boolean {
        return requestUrl.search('localhost') === -1
            && requestUrl.search('AnA-backend/private/verifyClosedSession') === -1
            && requestUrl.search('AnA-backend/public/authorization/isAuthorized') === -1;
    }

    private notifySessionExpired(): void {
        if (this.sesionExpired) {
            return;
        }
        this.sesionExpired = true;
        const modalRef = this.layoutUtilsService.generalAlert();
        modalRef.componentInstance.hasWarning = true;
        modalRef.componentInstance.data = this.translateService.instant('AUTH.GENERAL.SESION_EXPIRED');
        this.router.navigateByUrl('/auth/login');
    }

    private async handle(request: HttpRequest<any>, next: HttpHandler) {
        const username = (localStorage.getItem('user')) ? localStorage.getItem('user') : '';
        const angularURL = this.router.url.split('/');
        let componentAsParam = angularURL?.length > 3 ? '/' + angularURL[2] + '/' + angularURL[3] : '/' + angularURL[2];
        let applicationAsParam = window.location.href.replace(componentAsParam, '');
        if (applicationAsParam.includes('localhost')) {
            applicationAsParam = applicationAsParam.replace('http://localhost:4200/', environment.host);
        }
        // If request already provides context params, keep them (do not override).
        if (request.params.has('application')) {
            applicationAsParam = request.params.get('application');
        }
        if (request.params.has('component')) {
            componentAsParam = request.params.get('component');
        }
        const usernameAsParam = request.params.has('user') ? request.params.get('user') : username;
        // Mark as processed: only add Authorization, no user/component/application
        const isMarkAsProcessedEndpoint = request.url.indexOf('messages/process') !== -1;
        const authToken = localStorage.getItem(environment.authTokenKey) ? localStorage.getItem(environment.authTokenKey) : '';

        if (this.shouldValidateSession(request.url) && !authToken) {
            this.notifySessionExpired();
            return Promise.reject(new Error('Session expired: missing auth token'));
        }

        if (isMarkAsProcessedEndpoint) {
            request = request.clone({
                setHeaders: {
                    Authorization: authToken
                }
            });
        } else {
            request = request.clone({
                setHeaders: {
                    Authorization: authToken
                },
                setParams: {
                    user: usernameAsParam,
                    component: componentAsParam,
                    application: applicationAsParam
                }
            });
        }

        if (this.shouldValidateSession(request.url) && !this.sesionExpired) {
            try {
                const auth = await firstValueFrom(this.authService.verifyClosedSession());
                if (auth) {
                    this.notifySessionExpired();
                    return Promise.reject(new Error('Session expired: closed by backend'));
                }
            } catch (error) {
                if (error instanceof HttpErrorResponse && Number(error.status) === 401) {
                    this.notifySessionExpired();
                    return Promise.reject(error);
                }
            }
        }

        if (this.sesionExpired) {
            return Promise.reject(new Error('Session expired'));
        }
		if(componentAsParam == '/undefined' && applicationAsParam == environment.host && request.url.includes('AnA-backend/private/verifyClosedSession'))
		{
			//this.router.navigateByUrl('/auth/login');
			this.clearCache();
		}
        if(!localStorage.getItem('existException')) {
            return lastValueFrom(next.handle(request).pipe(
                tap(
                    event => {
                        if (event instanceof HttpResponse) {
                            // http response status code
                            const responseHttpStatus = Number(event.status);
                            // verifying if there's a session error
                            if (responseHttpStatus === 403) {
                                this.router.navigate(['error/403']);
                            }
                        }
                    },
                    error => {
                        // http response status code
                        if (error instanceof HttpErrorResponse) {
                            const responseHttpStatus = Number(error.status);
                            if (responseHttpStatus === 401) {
                                this.notifySessionExpired();
                            } else {
                                // console.error(error.error);
                                try {
                                    const auxException = (typeof error.error === 'string') ? JSON.parse(error.error) : error.error;
                                    const exception = { errors: auxException.errors, message: auxException.message, webService: auxException.webService };
                                    if (responseHttpStatus === 403) {
                                        this.router.navigate(['error/403'], {queryParams: { errors: JSON.stringify(exception) }});
                                        localStorage.setItem('existException', 'true');
                                        return throwError(error);
                                    } else {
                                        this.router.navigate(['error/404'], {queryParams: { errors: JSON.stringify(exception) }});
                                        localStorage.setItem('existException', 'true');
                                        return throwError(error);
                                    }
                                } catch (err) {
                                    if (responseHttpStatus === 403) {
                                        this.router.navigate(['error/403']);
                                        localStorage.setItem('existException', 'true');
                                        return throwError(error);
                                    } else {
                                        this.router.navigate(['error/404']);
                                        localStorage.setItem('existException', 'true');
                                        return throwError(error);
                                    }
                                }

                            }
                        }
                    }
                )
            ));
        }
    }

	async clearCache()
	{


		var theCookies = document.cookie.split(';');
for (var i = 1 ; i <= theCookies.length; i++) {
    var acookie = theCookies[i-1];
    var cookieArr = acookie.split('=');
    console.log(cookieArr[0]);
    document.cookie = cookieArr[0]+"=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

// Get cache storage and clear cache storage
window.caches.keys().then(function(names) {
    for (let name of names)
        window.caches.delete(name);
});

// // Get indexed db and delete indexed db
// const dbs = await window.indexedDB.databases()
// dbs.forEach(db => { window.indexedDB.deleteDatabase(db.name) })

// clear localStorage
window.localStorage.clear();

// clear sessionStorage
window.sessionStorage.clear();
this.router.navigate(['/']);
	}

}
